<template>
  <div v-if="appStore.showPaywall" class="paywall">
    <a-modal
      v-model:open="purchaseStore.loadingPaywall"
      :closable="false"
      :width="200"
      :footer="null"
      centered
      :z-index="10000"
    >
      <div class="paywall__modal-content">
        <van-loading
          class="paywall__modal-content-icon"
          type="spinner"
          :color="colors.brand03"
        />
      </div>
    </a-modal>
    <van-action-sheet
      class="paywall__container"
      :show="appStore.showPaywall"
      @close="onClose()"
    >
      <component :is="componentVersion" @close="onClose" @on-buy="onBuy" />
    </van-action-sheet>
  </div>

  <div v-if="props.isFullscreen" class="paywall">
    <van-action-sheet class="paywall__container" :show="true">
      <component
        :is="componentVersion"
        :is-fullscreen="props.isFullscreen"
        @close="onClose"
        @on-buy="onBuy"
      />
    </van-action-sheet>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

import getAbTest from '@/helpers/abTests/get-ab-test';
import getCustomColor from '@/helpers/get-custom-color';
import { updateStoreProducts } from '@/helpers/initiate-in-app-purchases';
import { tracking } from '@/helpers/track-event';
import logger from '@/infra/logger';
import { useAppStore } from '@/store/app';
import { usePurchaseStore } from '@/store/purchase';
import { EventName } from '@/types/events';

import VersionA from './versions/VersionA.vue';
import VersionB from './versions/VersionB.vue';

const purchaseStore = usePurchaseStore();
const appStore = useAppStore();

export type PaywallVersion = 'VersionA' | 'VersionB';
type PaywallProps = {
  isFullscreen?: boolean;
};

const props = withDefaults(defineProps<PaywallProps>(), {
  isFullscreen: false,
});

const versions: { [key in PaywallVersion]: typeof VersionA | typeof VersionB } =
  {
    VersionA,
    VersionB,
  };

const defaultVersion: PaywallVersion = 'VersionA';
const version = ref<PaywallVersion>(defaultVersion);

onMounted(() => {
  tracking.init();

  const abTest = getAbTest('PAYWALL');

  switch (abTest) {
    case 'versionA':
      version.value = 'VersionA';
      break;

    case 'versionB':
      version.value = 'VersionB';
      break;

    default:
      version.value = defaultVersion;
  }
});

const componentVersion = computed(() => versions[version.value]);

const colors = computed(() => ({
  brand03: getCustomColor('--color-brand-03'),
}));

function onClose() {
  appStore.setShowPaywall(false);

  setTimeout(() => {
    purchaseStore.setLoadingPaywall(false);
  }, 1000);
}

watch(
  () => appStore.showPaywall,
  (value) => {
    if (value) {
      tracking.trackEvent(EventName.OpenPaywall);
    } else {
      tracking.trackEvent(EventName.ClosePaywall);
    }
  },
);

async function onBuy() {
  purchaseStore.setLoadingPaywall(true);

  if (
    !purchaseStore.CdvPurchaseStore ||
    !purchaseStore.products ||
    purchaseStore.products.length === 0
  ) {
    const CdvPurchaseStore = new window.CdvPurchase.Store();

    updateStoreProducts({
      CdvPurchaseStore,
      subscriptions: purchaseStore.availableSubscriptions,
    });

    if (
      !purchaseStore.CdvPurchaseStore ||
      !purchaseStore.products ||
      purchaseStore.products.length === 0
    ) {
      const error = new Error('iap not working');
      logger.captureException(error);
      purchaseStore.setLoadingPaywall(false);
      appStore.setFullscreenLoading({
        show: false,
        texts: [],
      });

      alert('Não é possível iniciar o processo de compra, conclua pelo site.');
      window.open('https://link.beconfident.app/app-sign-up', '_self');
    }

    return;
  }

  const productId = purchaseStore.products[0].offer.productId;
  const offer = purchaseStore.products[0].offer as CdvPurchase.Offer;

  tracking.trackEvent(EventName.InitiateCheckout, { productId, offer });

  try {
    await purchaseStore.CdvPurchaseStore?.order(offer);
  } finally {
    purchaseStore.setLoadingPaywall(false);
  }
}
</script>

<style lang="scss" scoped>
.paywall {
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10000;

  :deep(.paywall__container) {
    height: 100vh;
    background-color: var(--color-brand-05);
    bottom: auto;
    max-height: 100vh;
    padding-bottom: 0px !important;
  }
}

.paywall__modal-content {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100px;

  &-icon {
    margin: 32px 0;
  }
}
</style>
